import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../services/AuthContext';
import { useNotifications } from '../services/NotificationContext';
import Navbar from '../components/Navbar';
import '../styles/Messages.css';

function getConvoName(convo, currentUserId) {
  if (!convo) return '';
  if (convo.is_group) {
    if (convo.name) return convo.name;
    const others = (convo.conversation_members || [])
      .filter(m => m.user_id !== currentUserId)
      .map(m => m.profiles?.full_name)
      .filter(Boolean);
    return others.join(', ') || 'Group Chat';
  }
  const other = (convo.conversation_members || []).find(m => m.user_id !== currentUserId);
  return other?.profiles?.full_name || 'Unknown User';
}

function Conversation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { markConversationRead } = useNotifications();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const currentUserId = user?.user?.id || user?.id;

  const loadMessages = async () => {
    try {
      const res = await api.get(`/messages/conversations/${id}/messages`);
      setMessages(res.data.messages);
    } catch {}
  };

  const loadConversation = async () => {
    try {
      const res = await api.get(`/messages/conversations/${id}`);
      setConversation(res.data.conversation);
    } catch {}
  };

  useEffect(() => {
    loadConversation();
    loadMessages();
    markConversationRead(id);
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    setSending(true);
    const text = newMessage.trim();
    setNewMessage('');
    try {
      const res = await api.post(`/messages/conversations/${id}/messages`, { content: text });
      setMessages(prev => [...prev, res.data.message]);
    } catch {
      setNewMessage(text);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (ds) => {
    const d = new Date(ds);
    const now = new Date();
    const diffH = (now - d) / 3600000;
    if (diffH < 24) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const convoName = getConvoName(conversation, currentUserId);
  const members = (conversation?.conversation_members || [])
    .filter(m => m.user_id !== currentUserId)
    .map(m => m.profiles?.full_name)
    .filter(Boolean);

  return (
    <div className="page-container">
      <Navbar />
      <div className="conversation-container">
        <div className="conversation-back-header">
          <button className="back-btn" onClick={() => navigate('/messages')}>←</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>{convoName}</h2>
            {conversation?.is_group && members.length > 0 && (
              <span className="convo-member-chips">{members.join(', ')}</span>
            )}
          </div>
        </div>

        <div className="conversation-messages">
          {messages.map(msg => {
            const isOwn = msg.sender_id === currentUserId;
            return (
              <div key={msg.id} className={`message-row ${isOwn ? 'own' : 'other'}`}>
                {!isOwn && conversation?.is_group && (
                  <span className="message-sender-name">{msg.profiles?.full_name}</span>
                )}
                <div className={`message-bubble ${isOwn ? 'own' : 'other'}`}>
                  {msg.content}
                  <span className="message-time">{formatTime(msg.created_at)}</span>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSend} className="message-input-area">
          <input
            type="text"
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            placeholder="Message…"
            disabled={sending}
          />
          <button type="submit" className="btn-primary" disabled={!newMessage.trim() || sending}>
            Send
          </button>
        </form>
      </div>
    </div>
  );
}

export default Conversation;
